<template>
    <div class="group-avatar-stack" :style="containerStyle">
        <template v-if="groupImage">
            <WasabiImage :data="{ url: groupImage }" class="group-avatar-single" />
        </template>

        <template v-else>
            <!-- Layout 1 member -->
            <template v-if="displayMembers.length <= 1">
                <UserProfile v-if="displayMembers[0]?.image"
                    :data="{ title: displayMembers[0]?.name, image: displayMembers[0]?.image }" :width="containerSize"
                    :showDot="false" />
                <div v-else class="avatar-fallback avatar-fallback--single" :style="getFallbackStyle(0)">
                    {{ getInitial(displayMembers[0]?.name || groupName) }}
                </div>
            </template>

            <!-- Layout 2 members: Overlap -->
            <template v-else-if="displayMembers.length === 2">
                <!-- Avatar phía sau (góc trên phải) -->
                <div class="avatar-item" :style="getTwoMembersBackStyle">
                    <UserProfile v-if="displayMembers[0]?.image"
                        :data="{ title: displayMembers[0]?.name, image: displayMembers[0]?.image }"
                        :width="twoAvatarSize" :showDot="false" />
                    <div v-else class="avatar-fallback" :style="getFallbackStyle(0)">
                        {{ getInitial(displayMembers[0]?.name) }}
                    </div>
                </div>
                <!-- Avatar phía trước (góc dưới trái) -->
                <div class="avatar-item avatar-item--front" :style="getTwoMembersFrontStyle">
                    <UserProfile v-if="displayMembers[1]?.image"
                        :data="{ title: displayMembers[1]?.name, image: displayMembers[1]?.image }"
                        :width="twoAvatarSize" :showDot="false" />
                    <div v-else class="avatar-fallback" :style="getFallbackStyle(1)">
                        {{ getInitial(displayMembers[1]?.name) }}
                    </div>
                </div>
            </template>

            <!-- Layout 3 members: Hình tam giác -->
            <template v-else-if="displayMembers.length === 3">
                <div v-for="(member, index) in displayMembers" :key="member.id" class="avatar-item"
                    :style="getThreeMembersPosition(index)">
                    <UserProfile v-if="member.image" :data="{ title: member.name, image: member.image }"
                        :width="threeSize" :showDot="false" />
                    <div v-else class="avatar-fallback" :style="getFallbackStyle(index)">
                        {{ getInitial(member.name) }}
                    </div>
                </div>
            </template>

            <!-- Layout 4+ members: Grid 2x2 -->
            <template v-else>
                <template v-for="(member, index) in displayMembers.slice(0, 4)" :key="member.id || index">
                    <div class="avatar-item" :style="getFourMembersPosition(index)">
                        <template v-if="index === 3 && members.length > 3">
                            <div class="avatar-fallback avatar-fallback--more">
                                {{ remainingCountDisplay }}
                            </div>
                        </template>
                        <template v-else>
                            <UserProfile v-if="member.image" :data="{ title: member.name, image: member.image }"
                                :width="fourSize" :showDot="false" />
                            <div v-else class="avatar-fallback" :style="getFallbackStyle(index)">
                                {{ getInitial(member.name) }}
                            </div>
                        </template>
                    </div>
                </template>
            </template>
        </template>
    </div>
</template>

<script setup>
/* eslint-disable */
import { computed } from 'vue';
import UserProfile from '@/components/atom/UserProfile/UserProfile.vue';
import WasabiImage from '@/components/atom/WasabiIamgeCompp/WasabiIamgeCompp.vue';

const props = defineProps({
    groupImage: {
        type: String,
        default: ''
    },
    groupName: {
        type: String,
        default: ''
    },
    members: {
        type: Array,
        default: () => []
    },
    size: {
        type: Number,
        default: 48
    },
    maxDisplay: {
        type: Number,
        default: 4
    }
});

const scale = computed(() => props.size / 48);

const containerSize = computed(() => `${props.size}px`);
const thumbnail = computed(() => `${props.size}x${props.size}`);
const containerStyle = computed(() => ({
    width: containerSize.value,
    height: containerSize.value,
    position: 'relative'
}));

const twoAvatarSize = computed(() => `${Math.round(32 * scale.value)}px`);

const twoBackThumbnail = computed(() => {
    const s = Math.round(20 * scale.value);
    return `${s}x${s}`;
});
const twoFrontSize = computed(() => `${Math.round(32 * scale.value)}px`);
const twoFrontThumbnail = computed(() => {
    const s = Math.round(32 * scale.value);
    return `${s}x${s}`;
});

const getTwoMembersBackStyle = computed(() => ({
    position: 'absolute',
    right: '0',
    top: '0',
    width: twoAvatarSize.value,
    height: twoAvatarSize.value,
    zIndex: 1
}));

const getTwoMembersFrontStyle = computed(() => ({
    position: 'absolute',
    left: '0',
    bottom: '0',
    width: twoAvatarSize.value,
    height: twoAvatarSize.value,
    zIndex: 2,
    border: '2px solid #fff',
    borderRadius: '50%',
    boxSizing: 'content-box'
}));

// ========== LAYOUT 3 MEMBERS ==========
const threeSize = computed(() => `${Math.round(28 * scale.value)}px`);
const threeThumbnail = computed(() => {
    const s = Math.round(28 * scale.value);
    return `${s}x${s}`;
});

function getThreeMembersPosition(index) {
    const positions = [
        { left: Math.round(10 * scale.value), top: 0 },
        { left: 0, top: Math.round(20 * scale.value) },
        { left: Math.round(20 * scale.value), top: Math.round(20 * scale.value) }
    ];
    const pos = positions[index];
    return {
        position: 'absolute',
        left: `${pos.left}px`,
        top: `${pos.top}px`,
        width: threeSize.value,
        height: threeSize.value
    };
}

// ========== LAYOUT 4+ MEMBERS ==========
const fourSize = computed(() => `${Math.round(24 * scale.value)}px`);
const fourThumbnail = computed(() => {
    const s = Math.round(24 * scale.value);
    return `${s}x${s}`;
});

function getFourMembersPosition(index) {
    const offset = Math.round(22 * scale.value);
    const positions = [
        { left: 0, top: 0 },
        { left: offset, top: 0 },
        { left: 0, top: offset },
        { left: offset, top: offset }
    ];
    const pos = positions[index];
    return {
        position: 'absolute',
        left: `${pos.left}px`,
        top: `${pos.top}px`,
        width: fourSize.value,
        height: fourSize.value
    };
}

// Members
const displayMembers = computed(() => {
    const uniqueMembers = [];
    const seenIds = new Set();

    for (const member of (props.members || [])) {
        if (member?.id && !seenIds.has(member.id)) {
            seenIds.add(member.id);
            uniqueMembers.push(member);
        }
        if (uniqueMembers.length >= props.maxDisplay) break;
    }

    return uniqueMembers;
});

const remainingCountDisplay = computed(() => {
    const total = props.members?.length || 0;
    if (total > 9) return '9+';
    return total.toString();
});

function getInitial(name) {
    if (!name) return '?';
    return name.trim().charAt(0).toUpperCase();
}

// Fallback colors
const fallbackColors = [
    { bg: '#4A90D9', text: '#fff' },
    { bg: '#7B68EE', text: '#fff' },
    { bg: '#20B2AA', text: '#fff' },
    { bg: '#FF8C00', text: '#fff' },
];

function getFallbackStyle(index) {
    const color = fallbackColors[index % fallbackColors.length];
    return {
        backgroundColor: color.bg,
        color: color.text
    };
}
</script>

<style scoped>
.group-avatar-stack {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.group-avatar-single {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    object-fit: cover;
}

.avatar-item {
    border-radius: 50%;
    overflow: hidden;
}

.avatar-item--front {
    border: 2px solid #fff;
    background: #fff;
}

.avatar-fallback {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 500;
    font-family: 'Inter', sans-serif;
    border-radius: 50%;
    letter-spacing: -0.02em;
    font-size: 12px;
    line-height: 16px;
}

.avatar-fallback--single {
    font-size: 24px;
    line-height: 32px;
}

.avatar-fallback--more {
    background-color: #E0E0E0;
    color: #666;
}
</style>